/**
 * Brera · Productos: carrusel, pestañas, "Cargar más" y páginas.
 * Lo usan sections/products.liquid, product-tabs.liquid, products-image.liquid y los carruseles de otras
 * secciones (colecciones, compra el look, confianza, blog). El producto destacado tiene su propio JS
 * (assets/featured-product.js).
 * - Carrusel: flechas (a los lados o junto al título), barra de avance, arrastrar con el mouse.
 * - Pestañas por colección.
 * - "Cargar más" y páginas: traen el siguiente tramo con la API de secciones (?section_id=) sin recargar.
 * Se vuelve a iniciar cuando el editor recarga la sección (shopify:section:load).
 */
(() => {
  if (window.BreraProducts) return;

  // Trae la sección en otra página (?page=N) y devuelve el documento
  const fetchSection = async (url, sectionId) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    const html = await fetch(u.toString()).then((r) => r.text());
    return new DOMParser().parseFromString(html, 'text/html');
  };

  /* ---------- Carrusel ---------- */
  const initSlider = (track) => {
    if (track.dataset.ready) return;
    track.dataset.ready = '1';

    const own = (scope, sel) => (scope ? [...scope.querySelectorAll(sel)].filter((el) => el.closest('[data-slider-root]') === scope) : []);
    const sliderRoot = track.closest('[data-slider-root]');
    let controlRoot = sliderRoot;
    if (!own(sliderRoot, '[data-slider-next]').length && sliderRoot && sliderRoot.parentElement) {
      controlRoot = sliderRoot.parentElement.closest('[data-slider-root]') || sliderRoot;
    }
    const prevs = own(controlRoot, '[data-slider-prev]');
    const nexts = own(controlRoot, '[data-slider-next]');
    const arrowGroups = own(controlRoot, '[data-slider-arrows]');
    const bar = own(sliderRoot, '[data-slider-progress]')[0];

    const step = () => {
      const item = track.children[0];
      if (!item) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const per = Math.max(1, Math.floor((track.clientWidth + gap + 2) / (item.offsetWidth + gap)));
      return per * (item.offsetWidth + gap);
    };

    prevs.forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: -step() })));
    nexts.forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: step() })));

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const x = track.scrollLeft;
      const scrollable = max > 2;
      prevs.forEach((b) => (b.disabled = x <= 2));
      nexts.forEach((b) => (b.disabled = x >= max - 2));
      arrowGroups.forEach((g) => (g.hidden = !scrollable));
      if (bar) {
        bar.hidden = !scrollable;
        const size = track.clientWidth / track.scrollWidth;
        bar.style.setProperty('--p-size', size * 100 + '%');
        bar.style.setProperty('--p-start', (scrollable ? (x / max) * (1 - size) : 0) * 100 + '%');
      }
    };
    track.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();

    // Arrastrar con el mouse (en celular se desliza solo)
    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) {
        moved = true;
        track.classList.add('is-dragging');
      }
      if (moved) track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      if (moved) {
        track.classList.remove('is-dragging');
        setTimeout(() => (moved = false), 0);
      }
    });
    track.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
    track.addEventListener('dragstart', (e) => e.preventDefault());
  };

  /* ---------- Pestañas ---------- */
  const selectTab = (tab) => {
    const list = tab.closest('[data-tabs]');
    if (!list) return;
    list.querySelectorAll('[role="tab"]').forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on);
      t.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    });
  };

  const initTabs = (list) => {
    if (list.dataset.ready) return;
    list.dataset.ready = '1';
    list.addEventListener('click', (e) => {
      const tab = e.target.closest('[role="tab"]');
      if (tab) selectTab(tab);
    });
    list.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const tabs = [...list.querySelectorAll('[role="tab"]')];
      const i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      next.focus();
      selectTab(next);
    });
  };

  /* ---------- Cargar más ---------- */
  // Texto "Has visto [x] de [y] productos" y barra de avance (si la sección los muestra)
  const updateCount = (btn, grid) => {
    const footer = btn.closest('[data-load-more-footer]');
    if (!footer) return;
    const total = Number(footer.dataset.total) || 0;
    const shown = Math.min(grid.querySelectorAll('[data-products-item]').length, total);
    const count = footer.querySelector('[data-load-more-count]');
    const bar = footer.querySelector('[data-load-more-bar]');
    if (count) count.textContent = count.dataset.template.replace('[x]', shown).replace('[y]', total);
    if (bar && total) bar.style.width = Math.round((shown / total) * 100) + '%';
  };

  const initLoadMore = (btn) => {
    if (btn.dataset.ready) return;
    btn.dataset.ready = '1';
    const section = btn.closest('[data-products-section]');
    btn.addEventListener('click', async () => {
      const grid = section.querySelector('[data-products-grid]');
      btn.classList.add('is-loading');
      try {
        const doc = await fetchSection(btn.dataset.nextPage, section.dataset.sectionId);
        doc.querySelectorAll('[data-products-grid] > [data-products-item]').forEach((li) => grid.appendChild(document.adoptNode(li)));
        const next = doc.querySelector('[data-load-more]');
        updateCount(btn, grid);
        if (next) btn.dataset.nextPage = next.dataset.nextPage;
        else btn.remove();
      } catch (e) {
        window.location.href = btn.dataset.nextPage;
      }
      btn.classList.remove('is-loading');
    });
  };

  /* ---------- Páginas ---------- */
  const initPagination = (section) => {
    if (section.dataset.pagesReady) return;
    section.dataset.pagesReady = '1';
    section.addEventListener('click', async (e) => {
      const link = e.target.closest('[data-pagination] a');
      if (!link) return;
      e.preventDefault();
      const page = section.querySelector('[data-products-page]');
      page.classList.add('is-loading');
      try {
        const doc = await fetchSection(link.href, section.dataset.sectionId);
        const fresh = doc.querySelector('[data-products-page]');
        if (fresh) page.replaceWith(document.adoptNode(fresh));
        window.history.replaceState({}, '', link.href);
        const top = section.getBoundingClientRect().top + window.scrollY - 80;
        if (top < window.scrollY) window.scrollTo({ top, behavior: 'smooth' });
      } catch (err) {
        window.location.href = link.href;
      }
    });
  };

  const init = (scope) => {
    scope.querySelectorAll('[data-slider-track]').forEach(initSlider);
    scope.querySelectorAll('[data-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-load-more]').forEach(initLoadMore);
    scope.querySelectorAll('[data-products-section]').forEach((s) => {
      if (s.querySelector('[data-pagination]')) initPagination(s);
    });
  };

  const start = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Editor del tema
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const tab = e.target.closest && e.target.closest('[role="tab"]');
    if (tab) selectTab(tab);
  });

  window.BreraProducts = { init };
})();
